import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { dashboardApi } from '@/services/dashboardApi';
import { useLoad, Loading, Table, Badge, Search, eur, num, fdate } from './ui';
import { useConfirm } from '../ConfirmDialog';

// Red de brokers (antes D_BROKERS + REG bloque 4)
export default function CdmRed() {
  const [dialog, ask] = useConfirm();
  const { data, error, reload } = useLoad(() => dashboardApi.cdmBrokers(), []);
  const links = useLoad(() => dashboardApi.cdmLinks(), []);
  const linkOf = useMemo(() => Object.fromEntries((links.data?.links || []).map((l) => [l.cdm_id, l])), [links.data]);
  const [q, setQ] = useState('');
  const rows = useMemo(() => (data?.brokers || []).filter((b) => !q || [b.id, b.name, b.upline_id].some((v) => (v || '').toLowerCase().includes(q.toLowerCase()))), [data, q]);
  if (!data) return <Loading error={error} />;
  const editUpline = async (b) => {
    const v = await ask({
      title: `Cambiar el upline de ${b.id}${b.name ? ` · ${b.name}` : ''}`,
      details: [
        { label: 'Upline actual', value: b.upline_id ? `${b.upline_id}${b.upline_name ? ` · ${b.upline_name}` : ''}` : 'Raíz' },
        'El upline es el broker que está justo por encima. Cambia también su origen como socio (es el mismo dato).',
        'Las comisiones de N2 y N3 de sus ventas futuras se calculan con la red nueva.',
      ],
      input: { label: 'ID del nuevo upline', placeholder: 'B42 (vacío = raíz)', defaultValue: b.upline_id || '' },
      confirmLabel: 'Guardar upline', tone: 'gold',
    });
    if (v === false) return;
    const up = (v === true ? '' : v).trim().toUpperCase();
    if (up === (b.upline_id || '')) return;
    try { await dashboardApi.cdmPatchBroker(b.id, { upline_id: up || null }); toast.success(`Upline de ${b.id}: ${up || 'raíz'}`); reload(); } catch (e) { toast.error(e.message || 'No se ha podido guardar'); }
  };
  // Enlace con la cuenta de la plataforma: así el broker ve en su panel sus ventas, comisiones y red reales
  const editLink = async (b) => {
    const cur = linkOf[b.id];
    const v = await ask({
      title: `Cuenta de la plataforma de ${b.name || b.id}`,
      details: [
        { label: 'Enlazada ahora', value: cur?.email || 'Ninguna' },
        'Con el enlace, esa cuenta ve en su panel sus ventas, comisiones y red reales del CDM. Una cuenta solo puede tener una ficha.',
        cur ? 'Deja el email vacío para quitar el enlace (la ficha del CDM no cambia).' : null,
      ],
      input: { label: 'Email de la cuenta', type: 'email', placeholder: 'nombre@dominio.com', defaultValue: cur?.email || b.email || '' },
      confirmLabel: 'Guardar enlace', tone: 'gold',
    });
    if (v === false) return;
    const email = (v === true ? '' : v).trim();
    try {
      if (email) { await dashboardApi.cdmLink(b.id, email); toast.success(`${b.id} enlazado con ${email}`); }
      else if (cur) { await dashboardApi.cdmUnlink(b.id); toast.success('Enlace quitado'); }
      links.reload();
    } catch (e) { toast.error(e.message || 'No se ha podido guardar'); }
  };
  const cols = [
    { key: 'id', label: 'ID', className: 'text-white font-medium' },
    { key: 'name', label: 'Broker', render: (b) => (<div><p className="text-white">{b.name}</p><p className="text-xs text-gray-400">desde {fdate(b.since)}</p></div>) },
    { key: 'upline_id', label: 'Upline', render: (b) => (
      <button onClick={() => editUpline(b)} className="text-left hover:text-white" title="Cambiar upline">{b.upline_id || 'Raíz'}<p className="text-xs text-gray-400">{b.upline_name || ''}</p></button>) },
    { key: 'link', label: 'Cuenta plataforma', sort: (b) => linkOf[b.id]?.email || '', render: (b) => (
      <button onClick={() => editLink(b)} className="text-left text-xs hover:text-white" title="Enlazar con su cuenta de la plataforma" data-testid={`cdm-link-${b.id}`}>
        {linkOf[b.id] ? <span className="text-emerald-300">{linkOf[b.id].email}</span> : <span className="text-[#c9a84c]">Enlazar</span>}
      </button>) },
    { key: 'status', label: 'Estado', render: (b) => <Badge tone={b.status === 'Activo' ? 'good' : b.status === 'Baja' ? 'bad' : 'neutral'}>{b.status}</Badge> },
    { key: 'level', label: 'Nivel', render: (b) => <Badge tone={b.level === 'Grandes cuentas' ? 'gold' : b.level === 'Profesional' ? 'info' : 'neutral'}>{b.level} ×{num(b.next_sale_multiplier, 1)}</Badge> },
    { key: 'portfolio_usd', label: 'Cartera (FHCT)', align: 'right', render: (b) => num(b.portfolio_usd) },
    { key: 'membership_sales', label: 'Ventas membresía', align: 'right' },
    { key: 'bricks_active', label: 'Bricks vigentes', align: 'right', render: (b) => num(b.bricks_active, 1) },
    { key: 'bricks_manual', label: 'Manuales', align: 'right', render: (b) => (b.bricks_manual ? num(b.bricks_manual, 1) : '—') },
    { key: 'bricks_revoked', label: 'Revocados', align: 'right', render: (b) => (b.bricks_revoked ? num(b.bricks_revoked, 1) : '—') },
    { key: 'fhct_commission_eur', label: 'Comisión FHCT', align: 'right', render: (b) => eur(b.fhct_commission_eur) },
  ];
  return (
    <div className="space-y-4" data-testid="cdm-red">
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <Search value={q} onChange={setQ} placeholder="Buscar broker o upline" />
        <p className="text-xs text-gray-400">Niveles por cartera: {data.levels.map((l) => `${l.name} desde ${num(l.from_usd)} (×${num(l.multiplier, 1)})`).join(' · ')}</p>
      </div>
      <Table cols={cols} rows={rows} empty="Sin brokers" />
      {dialog}
    </div>
  );
}
